"use client";

/**
 * Renders a generative-UI segment inside a chat message (tdd-0059 Phase 5).
 *
 * INJECTED, NOT IMPORTED. tdd-0012 asks for this explicitly, and the reason is
 * concrete: `apps/chat2/lib/render/` pulls in `@json-render/*`, the whole
 * component catalog, and every card implementation. Importing it here would
 * make that a hard dependency of the chat bundle — including for users who
 * never see a generated component — and would couple chat's release to chat2's
 * catalog. Instead the host registers a renderer at mount; with none
 * registered, a component segment degrades to a quiet placeholder rather than
 * a crash.
 *
 * That also keeps the chat2-retirement path open: when the renderer moves into
 * chat properly, only the registration call changes, not this seam.
 */

import { createContext, useContext, type ReactNode } from "react";

/** What a host must provide to render generative UI in the transcript. */
export interface ComponentRenderer {
  /**
   * Render a JSONL spec.
   *
   * @param jsonl    The raw patch stream from the fenced block.
   * @param opts.partial  True while the block is still streaming — the
   *                      renderer should show progress, not compile a
   *                      half-written spec into a broken tree.
   */
  render: (jsonl: string, opts: { partial: boolean }) => ReactNode;
}

const ComponentRendererContext = createContext<ComponentRenderer | null>(null);

export function ComponentRendererProvider({
  renderer,
  children,
}: {
  renderer: ComponentRenderer | null;
  children: ReactNode;
}) {
  return (
    <ComponentRendererContext.Provider value={renderer}>
      {children}
    </ComponentRendererContext.Provider>
  );
}

export function useComponentRenderer(): ComponentRenderer | null {
  return useContext(ComponentRendererContext);
}

/**
 * A single generative-UI block in the transcript.
 *
 * Three states, all of which must look deliberate rather than broken:
 *  - still streaming  → a quiet "building" line
 *  - no host renderer → a one-line note, never raw JSONL
 *  - ready            → delegate to the injected renderer
 */
export function ComponentSegment({
  jsonl,
  partial,
}: {
  jsonl: string;
  partial: boolean;
}) {
  const renderer = useComponentRenderer();

  if (partial) {
    return (
      <div className="my-2 flex items-center gap-2 text-sm text-muted-foreground">
        <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
        Building interface…
      </div>
    );
  }

  if (!renderer) {
    // Dumping the JSONL would be worse than saying nothing — it is long,
    // meaningless to a reader, and looks like a bug.
    return (
      <div className="my-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">
        This response contains an interactive component, which this view can&apos;t
        display.
      </div>
    );
  }

  return <div className="my-2">{renderer.render(jsonl, { partial })}</div>;
}
